// AIHandoffsBanner.jsx
// Persistent dashboard widget — lists every PENDING AI handoff alert until a
// staff member explicitly marks it resolved. The popup (AIHandoffAlert) fires
// once when the alert lands; this banner is the long-term home for it so the
// front desk / director / nurses can pick up alerts they missed.

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";

const C = {
  surface: "#ffffff",
  border: "#e8e2d8",
  red: "#b54040",
  redL: "rgba(181,64,64,0.10)",
  orange: "#e87d1f",
  orangeL: "rgba(232,125,31,0.10)",
  text: "#1a1a2e",
  muted: "#6b6356",
  light: "#f4f0ea",
  gold: "#b48c3c",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const VISIBLE_ROLES = new Set(["front_desk", "director", "admin", "owner", "manager", "nurse"]);

const REASON_LABEL = {
  unknown_question: "❓ Unknown question",
  treatment_complaint: "🩹 Treatment complaint",
  no_show_blocked: "🚫 No-show — routed to office",
  cant_resolve: "🤷 Couldn't resolve",
  manipulation: "⚠️ Hostile / abuse",
};

function formatPhone(p) {
  if (!p) return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return "(" + d.slice(0, 3) + ") " + d.slice(3, 6) + "-" + d.slice(6);
  if (d.length === 11 && d.startsWith("1")) return "+1 (" + d.slice(1, 4) + ") " + d.slice(4, 7) + "-" + d.slice(7);
  return p;
}

function timeAgo(ts) {
  if (!ts) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (s < 60) return s + "s ago";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  return Math.floor(s / 86400) + "d ago";
}

export default function AIHandoffsBanner() {
  // Pull auth from context — saves the caller from threading props down through
  // the CRM. The banner is a leaf widget; it owns its own auth lookup.
  const auth = useAuth() || {};
  const profile = auth.profile;
  const user = auth.user;
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [resolutionDraft, setResolutionDraft] = useState({});
  const [now, setNow] = useState(Date.now());
  // Debounced refresh so a burst of inserts doesn't spam the network
  const refreshTimerRef = useRef(null);

  const role = (profile?.role || "").toLowerCase();
  const isVisible = VISIBLE_ROLES.has(role);

  const load = async () => {
    const { data } = await supabase.from("ai_handoff_alerts")
      .select("*")
      .eq("status", "pending")
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false })
      .limit(50);
    setAlerts(data || []);
    setLoading(false);
  };

  const scheduleRefresh = () => {
    if (refreshTimerRef.current) return;
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      load();
    }, 800);
  };

  useEffect(() => {
    if (!isVisible) return;
    load();
    const ch = supabase.channel("ai-handoffs-banner-" + Math.random().toString(36).slice(2))
      .on("postgres_changes", { event: "*", schema: "public", table: "ai_handoff_alerts" }, scheduleRefresh)
      .subscribe();
    // Tick clock every 30s so "5m ago" labels stay fresh
    const tick = setInterval(() => setNow(Date.now()), 30000);
    return () => {
      supabase.removeChannel(ch);
      clearInterval(tick);
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const markResolved = async (alert) => {
    const notes = resolutionDraft[alert.id] || "";
    try {
      await supabase.from("ai_handoff_alerts").update({
        status: "resolved",
        resolved_at: new Date().toISOString(),
        resolved_by_name: profile?.full_name || profile?.email || "Staff",
        resolution_notes: notes || null,
        // Also fill acknowledged_* if it wasn't acknowledged yet
        ...(alert.acknowledged_at ? {} : {
          acknowledged_at: new Date().toISOString(),
          acknowledged_by_user_id: user?.id || null,
          acknowledged_by_name: profile?.full_name || profile?.email || "Staff",
        }),
      }).eq("id", alert.id);
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
      setExpandedId(null);
    } catch (e) {
      console.error("Resolve handoff failed:", e);
    }
  };

  const acknowledgeOnly = async (alert) => {
    if (alert.acknowledged_at) return;
    try {
      await supabase.from("ai_handoff_alerts").update({
        acknowledged_at: new Date().toISOString(),
        acknowledged_by_user_id: user?.id || null,
        acknowledged_by_name: profile?.full_name || profile?.email || "Staff",
      }).eq("id", alert.id);
      setAlerts(prev => prev.map(a => a.id === alert.id ? { ...a, acknowledged_at: new Date().toISOString() } : a));
    } catch (e) { console.error("Ack failed:", e); }
  };

  if (!isVisible || loading) return null;
  if (alerts.length === 0) return null;

  // touch `now` so the timeAgo labels re-render on the 30s tick
  void now;

  const highCount = alerts.filter(a => a.priority === 1).length;

  return (
    <div style={{
      background: C.surface,
      border: `2px solid ${highCount > 0 ? C.red : C.orange}`,
      borderRadius: 14,
      overflow: "hidden",
      marginBottom: 4,
      boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
    }}>
      <div style={{
        padding: "12px 18px",
        background: highCount > 0
          ? `linear-gradient(135deg, ${C.red}, ${C.red}dd)`
          : `linear-gradient(135deg, ${C.orange}, ${C.orange}dd)`,
        color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20, lineHeight: 1 }}>🚨🆘</span>
          <div>
            <div style={{ fontFamily: S, fontSize: 16, fontWeight: 800, letterSpacing: "0.02em" }}>
              AI Bot Handoffs — {alerts.length} pending
            </div>
            <div style={{ fontSize: 11, opacity: 0.9, fontWeight: 600 }}>
              {highCount > 0 && `${highCount} high-priority · `}
              clients waiting for human follow-up
            </div>
          </div>
        </div>
      </div>

      <div>
        {alerts.map(a => {
          const isHigh = a.priority === 1;
          const tel = (a.phone || "").replace(/[^\d+]/g, "");
          const isExpanded = expandedId === a.id;
          return (
            <div key={a.id} style={{
              borderBottom: `1px solid ${C.border}`,
              background: isHigh ? C.redL : (a.acknowledged_at ? "#fff" : C.orangeL),
              transition: "background 0.15s",
            }}>
              <div
                onClick={() => {
                  setExpandedId(isExpanded ? null : a.id);
                  if (!isExpanded) acknowledgeOnly(a);
                }}
                style={{ padding: "12px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
              >
                <div style={{
                  fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 99,
                  background: isHigh ? C.red : C.orange, color: "#fff", letterSpacing: "0.06em",
                  textTransform: "uppercase", flexShrink: 0,
                }}>
                  {isHigh ? "HIGH" : a.priority === 3 ? "LOW" : "Normal"}
                </div>

                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: C.text, fontFamily: S, marginBottom: 2 }}>
                    {a.client_name || "Unknown name"} <span style={{ color: C.muted, fontFamily: F, fontWeight: 500, fontSize: 12 }}>· {formatPhone(a.phone)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: C.text, fontWeight: 600 }}>
                    {REASON_LABEL[a.reason_type] || "Help needed"} — {a.reason_summary}
                  </div>
                </div>

                <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, whiteSpace: "nowrap" }}>
                  {timeAgo(a.created_at)}
                  {a.acknowledged_at && <span style={{ marginLeft: 8, color: C.gold }}>✓ seen</span>}
                </div>

                <a
                  href={`tel:${tel}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: "8px 14px", background: isHigh ? C.red : C.orange, color: "#fff",
                    borderRadius: 8, textDecoration: "none", fontSize: 12, fontWeight: 700,
                    fontFamily: F, letterSpacing: "0.03em", whiteSpace: "nowrap",
                  }}>📞 Call</a>
              </div>

              {isExpanded && (
                <div style={{ padding: "0 18px 16px 18px", background: "#fff" }}>
                  {a.client_message && (
                    <div style={{ padding: "10px 12px", background: C.light, borderLeft: `3px solid ${isHigh ? C.red : C.orange}`, borderRadius: 6, marginBottom: 12 }}>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>What the client said</div>
                      <div style={{ fontSize: 13, color: C.text, fontStyle: "italic", lineHeight: 1.45 }}>"{a.client_message}"</div>
                    </div>
                  )}

                  {a.bot_response_sent && (
                    <div style={{ padding: "8px 12px", background: C.light, borderRadius: 6, marginBottom: 12, fontSize: 11, color: C.muted }}>
                      <span style={{ fontWeight: 700 }}>Bot replied:</span> {a.bot_response_sent}
                    </div>
                  )}

                  <textarea
                    placeholder="Resolution notes (what did you do? called? booked? answered the question?)"
                    value={resolutionDraft[a.id] || ""}
                    onChange={e => setResolutionDraft(d => ({ ...d, [a.id]: e.target.value }))}
                    rows={2}
                    style={{
                      width: "100%", padding: "8px 10px", borderRadius: 6,
                      border: `1px solid ${C.border}`, fontFamily: F, fontSize: 12,
                      resize: "vertical", outline: "none", boxSizing: "border-box", marginBottom: 10,
                    }}
                  />

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      onClick={() => markResolved(a)}
                      style={{
                        padding: "9px 18px", background: "#2d8a55", color: "#fff",
                        border: "none", borderRadius: 8, fontSize: 12, fontWeight: 800,
                        cursor: "pointer", fontFamily: F, letterSpacing: "0.04em",
                      }}>✅ Mark Resolved</button>
                    <button
                      onClick={() => setExpandedId(null)}
                      style={{
                        padding: "9px 14px", background: "#fff", color: C.muted,
                        border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 12, fontWeight: 600,
                        cursor: "pointer", fontFamily: F,
                      }}>Close</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
